import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Upload, Loader2, Save, Film, Image as ImageIcon, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_LABELS = ["Étape 1 — Formulaire", "Étape 2 — Paiement", "Étape 3 — Rendez-vous", "Étape 4 — Confirmation"];

export default function AdminHowto() {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // "video" | 0..3
  const videoRef = useRef(null);
  const imgRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const load = useCallback(() => {
    axios.get(`${API}/howto`).then((r) => setDoc(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, []);
  useEffect(load, [load]);

  const upload = async (file, target) => {
    if (!file) return;
    setUploading(target);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      if (target === "video") {
        setDoc((d) => ({ ...d, video_url: data.url }));
      } else {
        setDoc((d) => {
          const imgs = [...d.steps_images];
          imgs[target] = data.url;
          return { ...d, steps_images: imgs };
        });
      }
      toast.success("Fichier téléversé — pensez à enregistrer");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/howto`, { video_url: doc.video_url, steps_images: doc.steps_images }, { withCredentials: true });
      toast.success("Page « Comment s'inscrire » mise à jour");
    } catch {
      toast.error("Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (!doc) return <p className="text-[#71717A] text-sm">Chargement…</p>;

  return (
    <div data-testid="admin-howto" className="space-y-10 max-w-4xl">
      <div>
        <h2 className="font-display text-xl uppercase font-bold">Tutoriel « Comment s'inscrire »</h2>
        <p className="text-xs text-[#71717A] mt-1">
          Gérez la vidéo et les captures affichées sur la page publique <a href="/comment-s-inscrire" target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:underline">/comment-s-inscrire</a>.
        </p>
      </div>

      {/* Vidéo */}
      <div className="border border-[#27272A] bg-[#121214] p-6" data-testid="howto-video-admin">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#71717A]"><Film size={14} className="text-[#C5A059]" /> Vidéo explicative</p>
          <div className="flex flex-wrap gap-3">
            <a href={doc.video_url} download="video-inscription-d2r.mp4" target="_blank" rel="noopener noreferrer" data-testid="howto-video-download"
              className="flex items-center gap-2 border border-[#C5A059] text-[#C5A059] px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-black transition-colors">
              <Download size={13} /> Télécharger la vidéo
            </a>
            <input ref={videoRef} type="file" accept="video/mp4" className="hidden" data-testid="howto-video-input" onChange={(e) => upload(e.target.files[0], "video")} />
            <button onClick={() => videoRef.current?.click()} disabled={uploading === "video"} data-testid="howto-video-replace"
              className="flex items-center gap-2 border border-[#27272A] px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors disabled:opacity-50">
              {uploading === "video" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Remplacer (MP4, 60 Mo max)
            </button>
            {doc.video_url && (
              <button onClick={() => setDoc((d) => ({ ...d, video_url: "" }))} data-testid="howto-video-remove"
                className="flex items-center gap-2 border border-red-500/50 text-red-400 px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] hover:bg-red-500 hover:text-white transition-colors">
                <Trash2 size={13} /> Retirer la vidéo
              </button>
            )}
          </div>
        </div>
        {doc.video_url ? (
          <video key={doc.video_url} src={doc.video_url} controls playsInline className="w-56 border border-[#27272A]" data-testid="howto-video-preview" />
        ) : (
          <p className="text-xs text-red-400/80 border border-red-500/30 bg-red-500/5 px-4 py-3" data-testid="howto-video-removed">
            Aucune vidéo — la section vidéo est masquée sur la page publique. Téléversez une vidéo puis enregistrez pour la réafficher.
          </p>
        )}
      </div>

      {/* Captures des étapes */}
      <div className="border border-[#27272A] bg-[#121214] p-6" data-testid="howto-steps-admin">
        <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-5"><ImageIcon size={14} className="text-[#C5A059]" /> Captures des 4 étapes</p>
        <div className="grid sm:grid-cols-2 gap-5">
          {STEP_LABELS.map((label, i) => (
            <div key={i} className="border border-[#27272A] bg-[#0A0A0C] p-4" data-testid={`howto-step-admin-${i}`}>
              <p className="text-xs text-[#A1A1AA] mb-3">{label}</p>
              <img src={doc.steps_images[i]} alt={label} className="w-full h-36 object-cover object-top border border-[#27272A]" data-testid={`howto-step-preview-${i}`} />
              <input ref={imgRefs[i]} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`howto-step-input-${i}`} onChange={(e) => upload(e.target.files[0], i)} />
              <button onClick={() => imgRefs[i].current?.click()} disabled={uploading === i} data-testid={`howto-step-replace-${i}`}
                className="mt-3 flex items-center gap-2 border border-[#27272A] px-4 py-2 text-[10px] uppercase tracking-widest text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors disabled:opacity-50">
                {uploading === i ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Remplacer la capture
              </button>
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="howto-save"
        className="flex items-center gap-2 bg-[#C5A059] text-black px-8 py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Enregistrer
      </button>
    </div>
  );
}
